import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Network, Workflow, Brain, Phone, Database, TrendingUp } from 'lucide-react';

const features = [
  {
    id: 'multi-channel',
    icon: Network,
    title: 'Multi-Channel Support',
    description:
      'Deploy your AI assistant across SMS, voice calls, web chat, WhatsApp, and more—all from a single platform.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'flow-builder',
    icon: Workflow,
    title: 'Visual Flow Builder',
    description:
      'Drag-and-drop conversation flows without writing a single line of code. See your logic in action instantly.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    id: 'ai-powered',
    icon: Brain,
    title: 'AI-Powered Responses',
    description:
      'Leverage GPT-4 and Claude to create assistants that understand context and respond intelligently.',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    id: 'voice-sms',
    icon: Phone,
    title: 'Voice & SMS Integration',
    description:
      'Handle phone calls and text messages with the same assistant. Real-time transcription and natural voice synthesis included.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    id: 'knowledge-base',
    icon: Database,
    title: 'Knowledge Base',
    description:
      'Train your assistant on your documentation, FAQs, and knowledge base. Vector search ensures accurate responses.',
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    id: 'analytics',
    icon: TrendingUp,
    title: 'Real-Time Analytics',
    description:
      'Monitor performance, identify trends, and optimize your assistant with comprehensive analytics and reporting.',
    gradient: 'from-orange-500 to-red-500',
  },
];

export const Features: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section id="features" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent" />

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Everything You Need
            </span>
            <br />
            <span className="text-white">to Build Intelligent Assistants</span>
          </h2>
          <p
            className="text-xl text-gray-400 max-w-3xl mx-auto"
            style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
          >
            From visual flow builders to real-time analytics, we've got you covered
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <FeatureCard key={feature.id} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

interface FeatureCardProps {
  feature: typeof features[0];
  index: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, index }) => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const Icon = feature.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ scale: 1.05, y: -5 }}
      className="relative overflow-hidden rounded-2xl p-6 bg-gray-800/50 backdrop-blur-lg border border-gray-700 transition-all duration-300 cursor-pointer group"
    >
      {/* Animated gradient background */}
      <div
        className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity bg-gradient-to-br ${feature.gradient}`}
      />

      {/* Icon */}
      <motion.div
        whileHover={{ rotate: [0, -10, 10, 0] }}
        transition={{ duration: 0.5 }}
        className={`mb-4 inline-flex p-3 rounded-lg bg-gradient-to-br ${feature.gradient}`}
      >
        <Icon className="w-6 h-6 text-white" />
      </motion.div>

      {/* Content */}
      <h3
        className="text-xl font-semibold mb-2 text-white"
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        {feature.title}
      </h3>
      <p
        className="text-gray-400 leading-relaxed"
        style={{ fontFamily: "'DM Sans', 'Inter', sans-serif" }}
      >
        {feature.description}
      </p>

      {/* Glow effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="absolute inset-0 blur-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20" />
      </div>

      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.div>
  );
};
